import {
  CarWheelMeasurementType,
  CarWheelMeasurementTypeLabels,
  InspectionItemKind,
  type InspectionItemRecord,
  type InspectionSessionRecord
} from '@navio/nsdk';
import { keepPreviousData } from '@tanstack/react-query';
import { noddiAsync, URLKeys, type OrderingKeyOfUrlKey } from 'noddi-async';
import {
  ApiErrorPageInternal,
  NoddiColumnDef,
  NoddiTable,
  useNoddiSortOrderParams,
  usePaginationQueryParam
} from 'noddi-ui-web';
import {
  getUserDisplayName,
  INSPECTION_CONTEXT_LABEL_MAP,
  INSPECTION_PURPOSE_LABEL_MAP,
  NoddiDateFormats,
  noddiFormatDate,
  pluralizeWithValue
} from 'noddi-util';
import {
  useInspectionSessionTableFilters,
  type InspectionSessionTableScope
} from '../../hooks/filters/InspectionSession/useInspectionSessionTableFilters';
import { CarInfoCell } from '../CarDetails/Cards/CarInfoCell';
import { InspectionSourceIndicator } from './InspectionSourceIndicator';

const MEASUREMENT_TYPES = [
  CarWheelMeasurementType.TREAD_DEPTH,
  CarWheelMeasurementType.AIR_PRESSURE,
  CarWheelMeasurementType.TPMS_BATTERY_LEVEL
] as const;

/** "Tread depth 8 · 4 photos · 4 specifications", so a row is not only a count. */
const observationSummary = (items: InspectionItemRecord[] | undefined): string => {
  if (!items?.length) {
    return 'No observations';
  }
  const parts: string[] = [];
  const knownTypes = new Set<CarWheelMeasurementType>(MEASUREMENT_TYPES);
  for (const type of MEASUREMENT_TYPES) {
    const count = items.filter(
      (item) => item.kind === InspectionItemKind.MEASUREMENT && item.measurementType === type
    ).length;
    if (count > 0) {
      parts.push(`${CarWheelMeasurementTypeLabels[type]} ${count}`);
    }
  }
  const otherMeasurements = items.filter(
    (item) =>
      item.kind === InspectionItemKind.MEASUREMENT &&
      (item.measurementType == null || !knownTypes.has(item.measurementType))
  ).length;
  if (otherMeasurements > 0) {
    parts.push(pluralizeWithValue({ value: otherMeasurements, word: 'measurement' }));
  }
  const photos = items.filter((item) => item.kind === InspectionItemKind.PHOTO).length;
  if (photos > 0) {
    parts.push(pluralizeWithValue({ value: photos, word: 'photo' }));
  }
  const specifications = items.filter((item) => item.kind === InspectionItemKind.SPEC).length;
  if (specifications > 0) {
    parts.push(pluralizeWithValue({ value: specifications, word: 'specification' }));
  }
  const skips = items.filter((item) => item.kind === InspectionItemKind.SKIP).length;
  if (skips > 0) {
    parts.push(pluralizeWithValue({ value: skips, word: 'skip' }));
  }
  return parts.join(' · ');
};

type InspectionSessionOrderingKey = OrderingKeyOfUrlKey<typeof URLKeys.getPaginatedInspectionSessions>;

const columns: NoddiColumnDef<InspectionSessionRecord, unknown, InspectionSessionOrderingKey>[] = [
  {
    header: '',
    id: 'source',
    size: 48,
    maxSize: 48,
    cell: ({ row }) => <InspectionSourceIndicator source={row.original.source} />
  },
  {
    header: 'Car',
    accessorFn: (row) => row.car?.licensePlate?.number ?? row.carId ?? row.id,
    cell: ({ row }) => (row.original.car ? <CarInfoCell car={row.original.car} /> : null)
  },
  {
    header: 'Purpose',
    accessorKey: 'purpose',
    cell: ({ row }) => INSPECTION_PURPOSE_LABEL_MAP[row.original.purpose]
  },
  {
    header: 'Context',
    accessorKey: 'context',
    cell: ({ row }) => INSPECTION_CONTEXT_LABEL_MAP[row.original.context]
  },
  {
    header: 'Observations',
    accessorFn: (row) => observationSummary(row.items),
    cell: ({ row }) => observationSummary(row.original.items)
  },
  {
    header: 'Created by',
    accessorFn: (row) => (row.createdBy ? getUserDisplayName(row.createdBy) : ''),
    cell: ({ row }) => (row.original.createdBy ? getUserDisplayName(row.original.createdBy) : null)
  },
  {
    header: 'Inspected at',
    accessorKey: 'inspectedAt',
    cell: ({ row }) => noddiFormatDate(row.original.inspectedAt, NoddiDateFormats.DATE_TIME),
    meta: { orderingKey: 'inspected_at' }
  }
];

type Props = {
  /** The session's own page in the calling app. */
  getSessionPath: (sessionId: number) => string;
  /**
   * Partner lists only its own departments' sessions, so the organization and department
   * controls would filter inside a scope the user cannot leave.
   */
  scope?: InspectionSessionTableScope;
};

export const InspectionSessionsTable = ({ getSessionPath, scope }: Props) => {
  const { pagination, setPagination } = usePaginationQueryParam();
  const { order, onOrderChange } = useNoddiSortOrderParams({ columns });
  const filters = useInspectionSessionTableFilters(scope);

  const {
    data: inspections,
    isPending,
    isFetching,
    error
  } = noddiAsync.useGet({
    type: URLKeys.getPaginatedInspectionSessions,
    input: {
      ...pagination,
      order,
      ...filters.apiInput
    },
    queryConfig: {
      placeholderData: keepPreviousData
    }
  });

  if (error) {
    return <ApiErrorPageInternal error={error} />;
  }

  return (
    <NoddiTable
      fillHeight
      data={inspections?.results}
      columns={columns}
      totalCount={inspections?.count}
      getRowId={(row) => row.id}
      isPending={isPending}
      isFetching={isFetching}
      pagination={pagination}
      setPagination={setPagination}
      ordering={order}
      onOrderChange={onOrderChange}
      filters={filters}
      searchPlaceholder='Search by license plate, make, or model'
      getUrlPath={(row) => ({
        url: getSessionPath(row.id)
      })}
    />
  );
};
